import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { i18next, setAppLocale, type AppLocale, type MessageKey } from '../i18n/index.js'

export type Locale = AppLocale
type LocaleT = (key: MessageKey) => string

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: LocaleT
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  return (
    <I18nextProvider i18n={i18next}>
      <LocaleContextBridge>{children}</LocaleContextBridge>
    </I18nextProvider>
  )
}

function LocaleContextBridge({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const [locale, setLocaleState] = useState<Locale>(() => (i18next.language === 'en' ? 'en' : 'zh'))

  useEffect(() => {
    const onLanguageChanged = (next: string) => setLocaleState(next === 'en' ? 'en' : 'zh')
    i18next.on('languageChanged', onLanguageChanged)
    return () => {
      i18next.off('languageChanged', onLanguageChanged)
    }
  }, [])

  const setLocale = useCallback((next: Locale) => {
    void setAppLocale(next)
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t: t as LocaleT,
    }),
    [locale, setLocale, t],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>')
  return ctx
}

export function LanguageToggle() {
  const { locale, setLocale, t } = useLocale()
  const options: { key: Locale; label: string }[] = [
    { key: 'zh', label: '中文' },
    { key: 'en', label: 'EN' },
  ]
  return (
    <div
      className="inline-flex flex-none overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)]"
      role="group"
      aria-label={t('aria.language')}
    >
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={locale === o.key}
          onClick={() => setLocale(o.key)}
          className={
            locale === o.key
              ? 'h-[26px] whitespace-nowrap bg-[var(--xr-brand-primary)] px-2 text-[11px] font-medium text-[var(--xr-brand-primary-ink)] sm:px-2.5'
              : 'h-[26px] whitespace-nowrap bg-[var(--xr-bg-surface)] px-2 text-[11px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] sm:px-2.5'
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
